import { useState } from "react";
import { toast } from "sonner";
import { Cpu, Loader2 } from "lucide-react";
import Segmented from "@/components/enhance/Segmented";
import { VERSION_ACTIVE, mins, trainVoice } from "@/lib/myVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const ABOUT = {
  quick: "A rough first listen, fastest and cheapest. Good to check your recordings work.",
  standard: "The everyday choice — natural tone, accent and phrasing.",
  best: "Longest training for the most detail. Worth it with 10+ minutes of singing.",
};

/** Section 2 — start a training run on Replicate. */
export default function TrainPanel({ voice, clips, versions, status, onStarted }) {
  const [quality, setQuality] = useState("standard");
  const [busy, setBusy] = useState(false);
  const ready = (clips || []).filter((c) => c.status === "ready");
  const training = (versions || []).some((v) => VERSION_ACTIVE.includes(v.status));
  const minSec = status?.min_sec || 120;
  const reason = !status?.connected ? "Connect Replicate to train." : training ? "Training is running — see Versions below."
    : voice.singing_sec < minSec ? `Add at least ${minSec / 60} minutes of clean singing first.` : null;
  const q = (status?.qualities || []).find((x) => x.key === quality);
  const go = async () => {
    setBusy(true);
    try { await trainVoice(voice.id, quality); toast.success("Training started", { description: "This takes a while — you can leave the page." }); onStarted(); }
    catch (e) { toast.error("Couldn't start training", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="train-panel" className="rounded-3xl border border-cyan-300/15 bg-[#081014]/80 p-5 sm:p-6">
      <h2 className="flex items-center gap-2 font-display text-base font-semibold md:text-lg"><Cpu className="h-4 w-4 text-cyan-300" /> Train your voice</h2>
      <p className="mt-1 text-[13px] text-white/55">Uses all {ready.length} ready recordings ({mins(voice.singing_sec)}, up to 30 min). Each run becomes a new version — older ones stay until you delete them.</p>
      <div className="mt-4">
        <Segmented label="Training quality" testId="train-quality" value={quality} onChange={setQuality}
          options={(status?.qualities || []).map((x) => [x.key, x.label])} />
        <p data-testid="train-quality-about" className="mt-2 text-[12px] text-white/50">{ABOUT[quality]}{q ? ` (${q.epochs} training passes)` : ""}</p>
      </div>
      <button type="button" data-testid="train-start" onClick={go} disabled={busy || Boolean(reason)}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-300 py-3 text-[14px] font-semibold text-black transition-transform hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />} Train version {(versions?.length || 0) + 1}
      </button>
      {reason ? <p data-testid="train-blocked-reason" className="mt-2 text-center text-[12px] text-white/50">{reason}</p> : null}
      <p className="mt-4 text-[11px] leading-relaxed text-white/40">Runs on your Replicate account, billed by GPU time. Your recordings are sent only for this run; Replicate deletes them within an hour. The finished model file is kept privately in your studio.</p>
    </section>
  );
}
